/**
 * @fileoverview useTransactionValidation - Validation error state management.
 *
 * Manages validation error state for both add and edit transaction forms.
 * Provides granular error clearing at field and category level.
 *
 * @description This hook provides:
 * - Error state for add form and edit form
 * - Field-level error clearing
 * - Category-level error clearing (with nested structure)
 * - Sum validation error clearing
 *
 * Error structure:
 * {
 *   amount: "Error message",
 *   description: "Error message",
 *   categories: {
 *     0: { category: "Error", amount: "Error" },
 *     1: { category: "Error" }
 *   },
 *   categorySum: "Sum error message"
 * }
 *
 * @module hooks/useTransactionValidation
 */
import { useState } from 'react';

/**
 * Hook for managing validation error state.
 *
 * @returns {Object} Error state and management functions
 * @returns {Object} return.errors - Add form errors
 * @returns {Function} return.setErrors - Set add form errors
 * @returns {Object} return.editErrors - Edit form errors
 * @returns {Function} return.setEditErrors - Set edit form errors
 * @returns {Function} return.clearError - Clear field error from add form
 * @returns {Function} return.clearCategoryError - Clear category error from add form
 * @returns {Function} return.clearEditError - Clear field error from edit form
 * @returns {Function} return.clearEditCategoryError - Clear category error from edit form
 *
 * @example
 * const validation = useTransactionValidation();
 * validation.setErrors({ amount: "Required" });
 * validation.clearError('amount');
 * validation.clearCategoryError(0, 'amount'); // Clear error for first category's amount
 */
export const useTransactionValidation = () => {
    // Error handling state
    const [errors, setErrors] = useState({});
    const [editErrors, setEditErrors] = useState({});

    // Error clearing functions for add form
    const clearError = (fieldName) => {
        if (errors[fieldName]) {
            setErrors({ ...errors, [fieldName]: undefined });
        }
    };

    const clearCategoryError = (categoryIndex, field) => {
        if (errors.categories && errors.categories[categoryIndex] && errors.categories[categoryIndex][field]) {
            const newErrors = { ...errors };
            delete newErrors.categories[categoryIndex][field];

            if (Object.keys(newErrors.categories[categoryIndex]).length === 0) {
                delete newErrors.categories[categoryIndex];
            }

            if (Object.keys(newErrors.categories).length === 0) {
                delete newErrors.categories;
            }

            setErrors(newErrors);
        }

        if (errors.categorySum) {
            setErrors({ ...errors, categorySum: undefined });
        }
    };

    // Error clearing functions for edit form
    const clearEditError = (fieldName) => {
        if (editErrors[fieldName]) {
            setEditErrors({ ...editErrors, [fieldName]: undefined });
        }
    };

    const clearEditCategoryError = (categoryIndex, field) => {
        if (editErrors.categories && editErrors.categories[categoryIndex] &&
            editErrors.categories[categoryIndex][field]) {
            const newErrors = { ...editErrors };
            delete newErrors.categories[categoryIndex][field];

            if (Object.keys(newErrors.categories[categoryIndex]).length === 0) {
                delete newErrors.categories[categoryIndex];
            }

            if (Object.keys(newErrors.categories).length === 0) {
                delete newErrors.categories;
            }

            setEditErrors(newErrors);
        }

        if (editErrors.categorySum) {
            setEditErrors({ ...editErrors, categorySum: undefined });
        }
    };

    return {
        // Error state
        errors,
        setErrors,
        editErrors,
        setEditErrors,

        // Error management
        clearError,
        clearCategoryError,
        clearEditError,
        clearEditCategoryError
    };
};
